import { callAdminApi } from './wixAdminApi';

export interface WixDayHours {
  open: string;  // "HH:MM"
  close: string; // "HH:MM"
}

export interface WixBusinessProfile {
  name?: string;
  tagline?: string;
  category?: string;
  logoUrl?: string;
  companyId?: string;
  email?: string;
  phone?: string;
  website?: string;
  address?: {
    line1?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  };
  hours?: {
    monday?: WixDayHours | null;
    tuesday?: WixDayHours | null;
    wednesday?: WixDayHours | null;
    thursday?: WixDayHours | null;
    friday?: WixDayHours | null;
    saturday?: WixDayHours | null;
    sunday?: WixDayHours | null;
  };
  serviceArea?: string[];
  _scheduleId?: string;
  _masterEvents?: Record<string, string>;
  _masterRevisions?: Record<string, string>;
  _masterAnchorDates?: Record<string, string>;
}

function wixLogoToUrl(logo: unknown): string | undefined {
  if (!logo) return undefined;
  if (typeof logo === 'string') {
    const clean = logo.replace(/^wix:image:\/\/v1\//, '').split('/')[0];
    return `https://static.wixstatic.com/media/${clean}`;
  }
  if (typeof logo === 'object') {
    const mediaId = (logo as Record<string, unknown>)['mediaId'] as string | undefined;
    if (!mediaId) return undefined;
    const clean = mediaId.replace(/^wix:image:\/\/v1\//, '').split('/')[0];
    return `https://static.wixstatic.com/media/${clean}`;
  }
  return undefined;
}

/** Just the weekly opening hours (one call, unlike getWixBusinessProfile). */
export async function getWixBusinessHours(): Promise<WixBusinessProfile['hours']> {
  const raw = await callAdminApi('getBusinessHours', 'businessInfo');
  return (raw as { hours?: WixBusinessProfile['hours'] }).hours;
}

export async function getWixBusinessProfile(): Promise<WixBusinessProfile> {
  const [siteRaw, hoursRaw] = await Promise.all([
    callAdminApi('getSiteProperties', 'businessInfo'),
    callAdminApi('getBusinessHours',  'businessInfo'),
  ]);

  const props = (siteRaw as Record<string, unknown>)['properties'] as Record<string, unknown> | undefined;
  if (!props) return {};

  const addr         = props['address'] as Record<string, unknown> | undefined;
  const streetNumber = (addr?.['streetNumber'] as string) || '';
  const street       = (addr?.['street'] as string) || '';
  const apt          = (addr?.['apartmentNumber'] as string) || '';
  const streetLine   = [streetNumber, street].filter(Boolean).join(' ');
  const line1        = [streetLine, apt].filter(Boolean).join(', ') || undefined;

  const categories = props['categories'] as Record<string, unknown> | undefined;
  const secondary  = (categories?.['secondary'] as string[]) ?? [];
  const category   = secondary[0] || (categories?.['primary'] as string) || undefined;

  const { scheduleId, masterEvents, masterRevisions, masterAnchorDates, hours } = hoursRaw as {
    scheduleId:        string | null;
    masterEvents:      Record<string, string>;
    masterRevisions:   Record<string, string>;
    masterAnchorDates: Record<string, string>;
    hours:             WixBusinessProfile['hours'];
  };

  return {
    name:      (props['businessName'] as string)    || undefined,
    tagline:   (props['description'] as string)     || undefined,
    category,
    logoUrl:   wixLogoToUrl(props['logo']),
    companyId: (props['companyId'] as string)       || undefined,
    email:     (props['email'] as string)           || undefined,
    phone:     (props['phone'] as string)           || undefined,
    website:   (props['externalSiteUrl'] as string) || undefined,
    address: addr ? {
      line1,
      city:    (addr['city'] as string)    || undefined,
      state:   (addr['state'] as string)   || undefined,
      zip:     (addr['zip'] as string)     || undefined,
      country: (addr['country'] as string) || undefined,
    } : undefined,
    hours,
    serviceArea:         [],
    _scheduleId:         scheduleId ?? undefined,
    _masterEvents:       masterEvents,
    _masterRevisions:    masterRevisions,
    _masterAnchorDates:  masterAnchorDates,
  };
}

export async function updateWixBusinessSchedule(
  hours: WixBusinessProfile['hours'],
  scheduleId: string | undefined,
  masterEvents: Record<string, string> | undefined,
  masterRevisions: Record<string, string> | undefined,
  masterAnchorDates: Record<string, string> | undefined,
): Promise<void> {
  await callAdminApi('updateBookingsSchedule', 'businessInfo', {
    scheduleId:        scheduleId ?? null,
    masterEvents:      masterEvents ?? {},
    masterRevisions:   masterRevisions ?? {},
    masterAnchorDates: masterAnchorDates ?? {},
    hours,
  });
}
